/**
 * router/utilities/index.ts
 * desc: utility functions for the router
 * note: isRouteAuthorized is the one authorization check for a route configuration, shared by AuthorizedRoute
 *       and the navigation — no auth block, or `permissions: null`, marks an unprotected route;
 *       `required` must ALL match, `permissions` ANY.
 *       findRoute and getBreadcrumbs read the route table for a location: the matched route, and for the header
 *       breadcrumb its declared `breadcrumb.parent` chain up to Home — nothing is inferred from the URL shape
 */

import { matchRoutes, type Params, type RouteObject } from 'react-router';
import routes from '../../routes';
import appUrls from '../../settings/app-urls';
import { interpolateAppUrl } from '../../utilities';
import { hasAccess } from '../../utilities/authentication';
import type { AppRoute, AppUrlKey, BreadcrumbEntity, RouteAuth } from '../types';

export interface Crumb {
    // where the crumb links to — the route's path filled with the location's params
    path: string;
    // a fixed label …
    labelKey?: string;
    // … or the entity the crumb is named after, resolved by the breadcrumb from that entity's data
    entity?: { kind: BreadcrumbEntity; id: string; params: Params };
}

type PathRoute = AppRoute & { path: string };

// the same table AppRouter renders; react-router's matcher ranks a static segment above a param, so
// /components/rules/create is the create route and not the rule called "create"
const pathRoutes = routes.appRoutes.filter((route): route is PathRoute => !!route.path);
const routeObjects: RouteObject[] = pathRoutes.map((route) => ({ path: route.path, handle: route }));
const routesByPath = new Map(pathRoutes.map((route) => [ route.path, route ]));

export function isRouteAuthorized(route: Pick<AppRoute, 'auth'>, userPermissions: string[] = []): boolean {
    const { permissions = null, required = [] }: RouteAuth = route.auth || {};
    return hasAccess(permissions, userPermissions, required);
}

const _match = (pathname: string) => matchRoutes(routeObjects, pathname)?.[0];

/**
 * findRoute
 * desc: the route serving a location — undefined when none does
 */
export function findRoute(pathname: string): AppRoute | undefined {
    return _match(pathname)?.route.handle as PathRoute | undefined;
}

// the deepest param of a pattern identifies the entity of that level (/step-factories/:id/versions/:versionId → versionId)
const _entityId = (pattern: string, params: Params): string => {
    const names = [ ...pattern.matchAll(/:(\w+)\??/g) ].map((match) => match[1]);
    return params[names.at(-1) ?? ''] ?? '';
};

const _toCrumb = (route: PathRoute, params: Params): Crumb | null => {
    const config = route.breadcrumb;
    if (!config) return null;

    const path = interpolateAppUrl(route.path, params);
    if ('entity' in config) return { path, entity: { kind: config.entity, id: _entityId(route.path, params), params } };

    return { path, labelKey: config.labelKey };
};

/**
 * getBreadcrumbs
 * desc: the crumbs of a location, root first and the current page last — empty when no route serves it
 */
export function getBreadcrumbs(pathname: string): Crumb[] {
    const match = _match(pathname);
    if (!match) return [];

    const crumbs: Crumb[] = [];
    // a `parent` chain that loops would otherwise never end
    const visited = new Set<PathRoute>();
    let route: PathRoute | undefined = match.route.handle as PathRoute;
    while (route && !visited.has(route)) {
        visited.add(route);
        const crumb = _toCrumb(route, match.params);
        if (crumb) crumbs.unshift(crumb);

        // annotated: inferred from `route`, it would feed the next `route` and trip the circularity check
        const parent: AppUrlKey | undefined = route.breadcrumb?.parent;
        route = parent ? routesByPath.get(appUrls[parent]) : undefined;
    }

    return crumbs;
}
